"use client";

import { type ReactNode, useEffect, useState } from "react";
import { Hint, Strong } from "@/components/ui/design-system/text";
import { cn } from "@/lib/utils";

/**
 * The shape every /join link preview settles into: a card hung directly off
 * its input, holding a picture, a quiet label, and the thing worth reading
 * under it. A card rather than loose lines because loose lines under a field
 * read as more hint text; a surface butted up against the input reads as what
 * the input produced, the way a pasted link unfurls in a message.
 * Two lines rather than one sentence
 * because the handle fields share a row, and a sentence truncated at half
 * width cuts off the name, which is the part the applicant is checking. The
 * value wraps rather than truncates for the same reason; `clamp` caps it for
 * page titles, which can run to a paragraph.
 *
 * A person's picture is round and a page's is a rounded square, the way the
 * platforms themselves draw them, so a profile and a page never read as the
 * same kind of thing.
 */
export function LinkPreviewRow({
  imageUrl,
  imageAlt,
  imageShape,
  labelIconUrl = null,
  label,
  value,
  clamp = false,
}: {
  imageUrl: string | null;
  imageAlt: string;
  imageShape: "person" | "page";
  /** A site's favicon, beside its name the way a browser tab shows it. */
  labelIconUrl?: string | null;
  label: ReactNode;
  value: ReactNode;
  clamp?: boolean;
}) {
  const [imageFailed, setImageFailed] = useState(false);
  const [iconFailed, setIconFailed] = useState(false);
  // A different link's picture may load where the last one failed.
  useEffect(() => setImageFailed(false), [imageUrl]);
  useEffect(() => setIconFailed(false), [labelIconUrl]);

  return (
    <PreviewMessage>
      <span className="flex items-center gap-nf-ds-3 rounded-nf-ds-md border border-border bg-nf-ds-bg-muted px-nf-ds-3 py-nf-ds-2">
        {imageUrl && !imageFailed ? (
          // biome-ignore lint/performance/noImgElement: third-party og:image or CDN URL, not in the Next image allowlist
          <img
            src={imageUrl}
            alt={imageAlt}
            referrerPolicy="no-referrer"
            onError={() => setImageFailed(true)}
            className={cn(
              "size-nf-ds-8 shrink-0 bg-nf-ds-bg-surface object-cover",
              imageShape === "person"
                ? "rounded-nf-ds-pill"
                : "rounded-nf-ds-sm",
            )}
          />
        ) : null}
        <span className="flex min-w-0 flex-col">
          <span className="flex min-w-0 items-center gap-nf-ds-1">
            {labelIconUrl && !iconFailed ? (
              // biome-ignore lint/performance/noImgElement: a site's own favicon, any host, not in the Next image allowlist
              <img
                src={labelIconUrl}
                alt=""
                referrerPolicy="no-referrer"
                onError={() => setIconFailed(true)}
                className="size-nf-ds-4 shrink-0 rounded-nf-ds-sm object-contain"
              />
            ) : null}
            <span className="truncate">{label}</span>
          </span>
          <Strong
            className={cn("break-words text-nf-ds-fg", clamp && "line-clamp-2")}
          >
            {value}
          </Strong>
        </span>
      </span>
    </PreviewMessage>
  );
}

/**
 * One line under a field, for every preview state that isn't a result. The
 * live region wraps the text because the DS text primitives don't forward aria
 * attributes. Polite, so a lookup finishing never interrupts someone still
 * typing.
 */
export function PreviewMessage({
  children,
  warn = false,
}: {
  children: ReactNode;
  warn?: boolean;
}) {
  return (
    <div aria-live="polite">
      <Hint
        as="div"
        muted={!warn}
        className={warn ? "text-nf-ds-tone-warn-fg" : undefined}
      >
        {children}
      </Hint>
    </div>
  );
}
